import type { ReactNode } from "react";
import type { PlanState, TrialOffer } from "../../lib/api";
import { SystemRow } from "../knock/chat";
import { Button, QuietButton } from "../ui/button";
import { UPDATE_CARD, useBillingPortal } from "./manage";
import { SEE_THE_PLANS, START_TRIAL, trialOfferLine } from "./trial-gate";

/**
 * **THE USAGE NOTICE** (ruling R-0921-2 (9), (10)).
 *
 * One line and one control, in two places: the console, where an owner lives,
 * and the top of an open knock, where a sender is about to ask for more work.
 * From 80% of the period it says the limit is being approached; at the wall it
 * says it is used. Both sentences are the SERVER'S — they arrive on the plan
 * payload — and so is the control's label, because which options a tier has is
 * the server's to decide and "Get more usage" and "Start a plan" are different
 * promises.
 *
 * **NOT ONE FIGURE IN IT** (ruling C2). No capacity, no floor, no share, no
 * price. The ring beside it already says how full the period is, as a share;
 * this says what to do about it.
 *
 * **PURE PROPS, LIKE `PlanCard`**, and for the same reason it is: this renders
 * inside the console, which has a query client and a shell, AND at the top of a
 * sender's knock page, which is a public route with neither. What the two have
 * in common is a plan state; how each of them came by one is their own business.
 *
 * It draws nothing at all where there is no notice, and nothing on an unmetered
 * tier, where the whole sentence would be false — a Free door has no monthly
 * usage limit to approach (ruling C10), it does no AI work.
 */
export type UsageNoticeProps = {
  /** The plan as the server sent it, or null before it has arrived. */
  plan: PlanState | null | undefined;
  where: "console" | "knock";
  /**
   * What the control opens — the plan sheet, wherever the caller keeps one. A
   * notice with no handler still says its line, which is the half that matters.
   */
  onAction?: (() => void) | undefined;
};

/**
 * **THE WALL ON A PLAN WHOSE PAYMENT IS FAILING** (ruling R-1006-3). No new
 * allowance opens while a payment is failing, so the one that was granted is
 * what is being used up, and when it is the way out is the card — not a bigger
 * plan, not a trial. One sentence and ONE control, "Update card", which opens
 * the processor's card page exactly as the payment notice in Manage does. No
 * upgrade offer, no trial offer, no "See the plans", and no figure.
 */
export const PAST_DUE_WALL_LINE = "Your payment didn't go through. Update your card to continue.";

/** Does this plan stand at the past-due wall's door? Metered, and past due. */
export function isPastDue(plan: PlanState | null | undefined): boolean {
  return plan?.metered === true && plan.state === "past_due";
}

export function UpdateCardButton({ className }: { className: string }) {
  const portal = useBillingPortal();
  return (
    <>
      <Button
        type="button"
        className={className}
        onClick={() => void portal.open("payment_method")}
        data-update-card-action
      >
        {UPDATE_CARD}
      </Button>
      {portal.problem ? (
        <span role="alert" className="w-full text-xs text-danger">
          {portal.problem}
        </span>
      ) : null}
    </>
  );
}

export function UsageNotice({ plan, where, onAction }: UsageNoticeProps) {
  if (!plan || !plan.metered) return null;
  if (plan.usage_notice === null || plan.usage_notice_line === null) return null;
  if (isPastDue(plan)) {
    // Approaching the end of what was granted says so and offers nothing; at
    // the wall it is the past-due wall.
    if (plan.usage_notice !== "used") {
      return (
        <div
          data-usage-notice={plan.usage_notice}
          data-where={where}
          role="status"
          className="mb-3 rounded-lg border border-line px-3 py-2 text-[13px]"
        >
          {plan.usage_notice_line}
        </div>
      );
    }
    return (
      <div
        data-usage-notice="used"
        data-past-due
        data-where={where}
        role="status"
        className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line px-3 py-2 text-[13px]"
      >
        <span>{PAST_DUE_WALL_LINE}</span>
        <UpdateCardButton className="h-8 shrink-0 px-3" />
      </div>
    );
  }

  return (
    <div
      data-usage-notice={plan.usage_notice}
      data-where={where}
      role="status"
      className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-line px-3 py-2 text-[13px]"
    >
      <span>{plan.usage_notice_line}</span>
      {onAction && plan.usage_notice_action ? (
        <button
          type="button"
          data-usage-notice-action
          className="shrink-0 font-medium underline underline-offset-2"
          onClick={onAction}
        >
          {plan.usage_notice_action}
        </button>
      ) : null}
    </div>
  );
}

/**
 * **THE WALL, INSIDE A CONVERSATION — ONE LINE AND ONE CONTROL** (owner,
 * 2026-09-23; ruling R-0921-2 (9); wave 57).
 *
 * "I thought we agreed on 'Get more usage' and then the options open up." Every
 * conversation that meets a spent allowance — the owner's chat with their own
 * Nob, the owner's chat on a knock, a sender's hearing, the compose pane's
 * knock start — used to draw the whole plan card at the point of refusal: the
 * rung above with its price, and a paid reset (since deleted, ruling R-0923-3
 * (1)). Now it says what it already
 * said and offers the one control the notice carries, with the same label from
 * the same field: "Get more usage", or "Start a plan" on a trial or on Free.
 * What pressing it opens is the plan sheet, scrolled to the options, and THAT
 * is where the prices and the consent sentences are.
 *
 * **NO PRICE, NO TIER NAME, NO SECOND BUTTON.** The line is the caller's —
 * each conversation keeps the sentence it already had — and the control is
 * this component's. With no handler, or no label from the server, the row
 * still says its line: the half that tells somebody what happened.
 */
export function SpentRow({
  line,
  plan,
  onAction,
  trial = null,
  onStartTrial,
}: {
  line: ReactNode;
  /** The plan as the server sent it — the control's label is on it. */
  plan: PlanState | null | undefined;
  /** Open the plan sheet at its options. */
  onAction?: (() => void) | undefined;
  /**
   * THE TRIAL OFFER, WHEN `GET /plan` REPORTS ONE (ruling R-1006-2). With one, and
   * a way to start it, the primary control is "Start your free trial" with the
   * offer on one line under it, and the paid plans move to a quiet "See the
   * plans". With none, the row is exactly what it was.
   */
  trial?: TrialOffer | null | undefined;
  /** Open the plan room at the trial's consent. */
  onStartTrial?: (() => void) | undefined;
}) {
  const label = plan?.usage_notice_action ?? null;
  // A FAILING PAYMENT'S WALL replaces the caller's line and every other
  // control (ruling R-1006-3): one sentence, one action, nothing to buy.
  if (isPastDue(plan)) {
    return (
      <SystemRow>
        <span
          className="flex flex-wrap items-center justify-center gap-3"
          data-spent-row
          data-past-due
        >
          <span>{PAST_DUE_WALL_LINE}</span>
          <UpdateCardButton className="h-8 shrink-0 px-3" />
        </span>
      </SystemRow>
    );
  }
  if (trial && onStartTrial) {
    return (
      <SystemRow>
        <span className="flex w-full flex-col items-center gap-2 text-center" data-spent-row>
          <span>{line}</span>
          <Button
            type="button"
            className="h-10 w-full max-w-xs"
            onClick={onStartTrial}
            data-trial-action
          >
            {START_TRIAL}
          </Button>
          <span className="text-xs text-muted" data-trial-line>
            {trialOfferLine(trial)}
          </span>
          {onAction ? (
            <QuietButton type="button" className="h-9 w-full max-w-xs" onClick={onAction}>
              {SEE_THE_PLANS}
            </QuietButton>
          ) : null}
        </span>
      </SystemRow>
    );
  }
  return (
    <SystemRow>
      <span className="flex flex-wrap items-center justify-center gap-3" data-spent-row>
        <span>{line}</span>
        {onAction && label ? (
          <Button type="button" className="h-8 shrink-0 px-3" onClick={onAction} data-usage-action>
            {label}
          </Button>
        ) : null}
      </span>
    </SystemRow>
  );
}
